import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, Table, Select, Button, DatePicker, message, Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsRotate, faCameraRetro } from "@fortawesome/free-solid-svg-icons";
import analyticsApi from "../../../api/analytics-api";
import formatMoney from "../../../utils/formatMoney";

// Bảng lịch sử số liệu ngày (DailyStats snapshot). Tự fetch dữ liệu riêng.
// Mặc định xem dòng tổng 'all'; đổi sàn để xem chi tiết. Cột user mới / rút tiền
// chỉ có ý nghĩa ở dòng 'all' (chỉ số toàn hệ thống).
//
// Nút máy ảnh: nếu đã chọn khoảng ngày → backfill khoảng đó; bỏ trống → chụp hôm qua.

const PLATFORM_OPTIONS = [
    { value: "all", label: "Tất cả (tổng)" },
    { value: "shopee", label: "Shopee" },
    { value: "lazada", label: "Lazada" },
    { value: "tiktok", label: "TikTok" },
];

const money = (v) => formatMoney.format(Number(v) || 0);
const num = (v) => (Number(v) || 0).toLocaleString("vi-VN");

function DashboardDailyStatsHistory() {
    const [platform, setPlatform] = useState("all");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [snapshotting, setSnapshotting] = useState(false);
    const [range, setRange] = useState(null); // [dayjs, dayjs] | null

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await analyticsApi.getDailyStats({ platform, limit: 120 });
            setRows(res.data.data || []);
        } catch (err) {
            console.error("[DailyStatsHistory] fetch error:", err);
            message.error("Không tải được lịch sử số liệu");
        } finally {
            setLoading(false);
        }
    }, [platform]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const onSnapshot = async () => {
        setSnapshotting(true);
        try {
            let res;
            if (range && range[0] && range[1]) {
                const from = range[0].format("YYYY-MM-DD");
                const to = range[1].format("YYYY-MM-DD");
                res = await analyticsApi.runDailyStatsSnapshot({ from, to });
                message.success(
                    `Đã chụp ${res.data.data?.ok}/${res.data.data?.days} ngày (${from} → ${to})`
                );
            } else {
                res = await analyticsApi.runDailyStatsSnapshot();
                message.success(`Đã chụp số liệu ngày ${res.data.data?.date}`);
            }
            fetchData();
        } catch (err) {
            console.error("[DailyStatsHistory] snapshot error:", err);
            message.error("Chụp số liệu thất bại");
        } finally {
            setSnapshotting(false);
        }
    };

    const columns = useMemo(() => {
        const base = [
            { title: "Ngày", dataIndex: "stat_date", key: "stat_date", fixed: "left", width: 110 },
            { title: "Đơn HT", dataIndex: "orders", key: "orders", align: "right", render: num },
            { title: "Chờ", dataIndex: "orders_pending", key: "orders_pending", align: "right", render: num },
            { title: "Hủy", dataIndex: "orders_cancelled", key: "orders_cancelled", align: "right", render: num },
            { title: "Buff", dataIndex: "buff_orders", key: "buff_orders", align: "right", render: num },
            { title: "HH sàn", dataIndex: "commission", key: "commission", align: "right", render: money },
            { title: "HH user", dataIndex: "user_commission", key: "user_commission", align: "right", render: money },
            { title: "HH ref", dataIndex: "ref_commission", key: "ref_commission", align: "right", render: money },
            { title: "Clicks", dataIndex: "clicks", key: "clicks", align: "right", render: num },
        ];
        if (platform === "all") {
            base.push(
                { title: "User mới", dataIndex: "new_users", key: "new_users", align: "right", render: num },
                { title: "User HĐ", dataIndex: "active_users", key: "active_users", align: "right", render: num },
                { title: "Lệnh rút", dataIndex: "withdraw_count", key: "withdraw_count", align: "right", render: num },
                { title: "Tiền rút", dataIndex: "withdraw_amount", key: "withdraw_amount", align: "right", render: money },
                { title: "Đã trả", dataIndex: "withdraw_paid_count", key: "withdraw_paid_count", align: "right", render: num },
                { title: "Tiền đã trả", dataIndex: "withdraw_paid_amount", key: "withdraw_paid_amount", align: "right", render: money },
                { title: "Thưởng", dataIndex: "bonus_amount", key: "bonus_amount", align: "right", render: money },
                { title: "Trừ tiền", dataIndex: "deduct_amount", key: "deduct_amount", align: "right", render: money },
                { title: "Đổi VC", dataIndex: "voucher_redeemed", key: "voucher_redeemed", align: "right", render: num },
                { title: "Cash đổi VC", dataIndex: "voucher_redeem_cost", key: "voucher_redeem_cost", align: "right", render: money },
                { title: "Số dư", dataIndex: "balance_total", key: "balance_total", align: "right", render: money }
            );
        }
        return base;
    }, [platform]);

    return (
        <Card
            title="Lịch sử số liệu theo ngày"
            className="shadow-sm"
            extra={
                <div className="flex items-center gap-2 flex-wrap">
                    <Select
                        value={platform}
                        onChange={setPlatform}
                        options={PLATFORM_OPTIONS}
                        size="small"
                        style={{ width: 150 }}
                    />
                    <DatePicker.RangePicker
                        size="small"
                        value={range}
                        onChange={setRange}
                        placeholder={["Backfill từ", "đến"]}
                    />
                    <Tooltip title={range ? "Backfill khoảng ngày đã chọn" : "Chụp số liệu hôm qua"}>
                        <Button
                            size="small"
                            icon={<FontAwesomeIcon icon={faCameraRetro} />}
                            loading={snapshotting}
                            onClick={onSnapshot}
                        />
                    </Tooltip>
                    <Tooltip title="Làm mới">
                        <Button
                            size="small"
                            icon={<FontAwesomeIcon icon={faArrowsRotate} />}
                            loading={loading}
                            onClick={fetchData}
                        />
                    </Tooltip>
                </div>
            }
        >
            <Table
                rowKey={(r) => `${r.stat_date}-${r.platform}`}
                columns={columns}
                dataSource={rows}
                loading={loading}
                size="small"
                scroll={{ x: "max-content" }}
                pagination={{ pageSize: 15, showSizeChanger: false }}
            />
        </Card>
    );
}

export default DashboardDailyStatsHistory;
